/**
 * HR-framework (hr_v1) magic-link tokens.
 *
 * Parallel collection to the OPTICS `reviewInvitations`. Same token-as-doc-ID
 * pattern (token IS the secret; `get` allowed, `list` denied by Firestore rule).
 *
 * Single dispatch route `/m/<token>` resolves to an action via this doc.
 * See CLAUDE.md §H.
 */

import type { Timestamp } from 'firebase/firestore';
import type { ReminderTag } from './magicLink';
import type { Review360Relationship, ReviewerVisibility } from './eprFramework';

// ─── Action kinds (§H.1) ────────────────────────────────────────────────────

export type MagicLinkAction =
  | 'epr_self_submit'        // Step 1 — employee submits self-score on EPR
  | 'epr_manager_submit'     // Step 1 — manager submits manager-score on EPR
  | 'review360_submit'       // Step 2 — 360 reviewer submits feedback
  | 'appraisal_sign'         // Step 5 — employee or manager signs the appraisal
  | 'result_view';           // any step — read-only view of own/team appraisal

export const MAGIC_LINK_ACTION_LABELS: Record<MagicLinkAction, string> = {
  epr_self_submit:      'Employee EPR self-assessment',
  epr_manager_submit:   'Manager EPR assessment',
  review360_submit:     '360 review submission',
  appraisal_sign:       'Appraisal sign-off',
  result_view:          'Result view (read-only)',
};

export type MagicLinkStatus = 'active' | 'used' | 'expired' | 'revoked';

export type MagicLinkRecipientRole =
  | 'employee'
  | 'manager'
  | Review360Relationship;

export type MagicLinkTargetCollection =
  | 'eprSubmissions'
  | 'review360Submissions'
  | 'appraisalForms';

// ─── Configurable expiry (§H.2) ─────────────────────────────────────────────

/**
 * HR-configurable expiry presets in days. Custom values are allowed at issue
 * time. Per CLAUDE.md §H.2.
 */
export const EXPIRY_PRESETS_DAYS = [7, 15, 30] as const;
export type ExpiryPresetDays = typeof EXPIRY_PRESETS_DAYS[number];

/** Default expiry per action when HR has not configured otherwise. */
export const DEFAULT_EXPIRY_DAYS: Record<MagicLinkAction, number> = {
  epr_self_submit:    15,
  epr_manager_submit: 15,
  review360_submit:   15,
  appraisal_sign:     15,
  result_view:        30,
};

/**
 * Per-cycle expiry configuration. Stored on the ReviewCycle (or its hr_v1
 * template) so HR can adjust per cycle. Anything missing falls back to
 * DEFAULT_EXPIRY_DAYS. Custom value (not in EXPIRY_PRESETS_DAYS) is allowed
 * but must pass `isReasonableExpiry()`.
 */
export interface CycleMagicLinkExpiry {
  epr_self_submit?:    number;
  epr_manager_submit?: number;
  review360_submit?:   number;
  appraisal_sign?:     number;
  result_view?:        number;
}

export function isReasonableExpiry(days: number): boolean {
  return Number.isInteger(days) && days >= 1 && days <= 90;
}

// ─── Token document ─────────────────────────────────────────────────────────

export interface MagicLinkToken {
  /** = the 24-byte hex token itself. Used as Firestore doc ID. */
  id: string;
  cycleId: string;
  action: MagicLinkAction;

  // Recipient
  recipientId: string;             // EPRUser.id or DirectoryEmployee.id
  recipientEmail: string;          // lowercased
  recipientName?: string;
  recipientRole: MagicLinkRecipientRole;

  // Target document this token authorises
  targetCollection: MagicLinkTargetCollection;
  targetDocId: string;

  // Lifecycle
  singleUse: boolean;              // false for result_view, true otherwise
  status: MagicLinkStatus;
  sentAt: Timestamp;
  expiresAt: Timestamp;
  openedAt?: Timestamp;
  usedAt?: Timestamp;
  revokedAt?: Timestamp;
  revokedReason?: string;
  remindersSent: ReminderTag[];

  // Email send + delivery telemetry (Resend integration).
  // emailSentAt / emailProviderMessageId are stamped by the
  // sendMagicLinkEmail Cloud Function when Resend acknowledges the send.
  // emailEvents fields are stamped later by the resendWebhook Cloud
  // Function as Resend reports lifecycle events. All optional — present
  // only when the corresponding event has fired.
  emailSentAt?:              Timestamp;  // we asked Resend to send
  emailProviderMessageId?:   string;     // Resend's id for webhook correlation
  emailDeliveredAt?:         Timestamp;  // recipient SMTP server accepted
  emailDeliveryDelayedAt?:   Timestamp;  // queue back-off in flight
  emailBouncedAt?:           Timestamp;  // hard bounce — invalid mailbox
  emailBounceReason?:        string;
  emailComplainedAt?:        Timestamp;  // recipient marked as spam
  emailOpenedAt?:            Timestamp;  // pixel fired (first open)
  emailClickedAt?:           Timestamp;  // recipient clicked the magic link
  // Pre-aggregated single label for at-a-glance UI. Derived from the
  // events above by the webhook so the HR Console doesn't have to.
  emailDeliveryStatus?: 'sent' | 'delivered' | 'opened' | 'clicked' | 'bounced' | 'complained' | 'delayed';

  /**
   * Snapshot of effective visibility at issue time. Set only when
   * action === 'review360_submit'. Read by the 360 form to render the
   * disclosure badge per CLAUDE.md §D.1.
   */
  effectiveVisibility?: ReviewerVisibility;

  /**
   * Who is being reviewed. Set only when action === 'review360_submit'.
   * Shown to the REVIEWER on their form header ("360 Review — <name>").
   * This is the reviewee's identity, not the reviewer's — §D.1 protects
   * reviewer identity on employee/manager surfaces; the reviewer must
   * obviously know who they are reviewing.
   */
  revieweeId?: string;
  revieweeName?: string;
}

// ─── Single-use policy ──────────────────────────────────────────────────────

/** Convenience helper — derives the single-use flag from the action kind. */
export function isSingleUseAction(action: MagicLinkAction): boolean {
  return action !== 'result_view';
}
